import React, { useState, useEffect } from "react";
import { ToastContainer, toast } from "react-toastify";
import "url-search-params-polyfill";
import "react-toastify/dist/ReactToastify.css";

import { generateQueryString } from "../../utils/queryString";

import InsightResult from "./InsightResult";
import SkeletonCard from "./SkeletonCard";
import getInsights from "../../lib/api/insightsApi";
import { IIndustryInsights } from "../../lib/models/IIndustryInsights";
import handleFilterChange from "../../utils/handleFilterChange";
import Filters from "../shared/Filters/Filters";

export default function IndustryInsights({ currentPageRef, pageSize }) {
  const urlParams = new URLSearchParams(window.location.search);

  const initialFilters = {
    page: parseInt(urlParams.get("page")) || 1,
    businessArea: urlParams.get("businessArea") || "",
    topic: urlParams.getAll("topic"),
    search: urlParams.get("search") || ""
  };

  const [filters, updateFilters] = useState(initialFilters);
  const [results, updateResults]: [IIndustryInsights, React.Dispatch<any>] =
    useState(null);

  const loadInsights = () => {
    getInsights(
      currentPageRef,
      filters.page,
      filters.search,
      filters.businessArea,
      filters.topic
    )
      .then((e) => {
        const clientQueryString = generateQueryString(filters, true);
        window.history.replaceState(null, null, `?${clientQueryString}`);
        if (filters.page === 1 || results === null) {
          updateResults(e);
        } else {
          updateResults({
            ...results,
            results: [...results.results, ...e.results]
          });
        }
      })
      .catch((e) => {
        console.error(e);
        return toast.error("Something went wrong. Please try again later");
      });
  };

  useEffect(loadInsights, [filters]);

  const handleFiltering = (filterValue) => {
    handleFilterChange("replace", filterValue, filters, updateFilters);
  };

  const renderResults = () => {
    if (results !== null) {
      return results.results.map((e, i) => (
        <InsightResult key={e.id} number={i} data={e} />
      ));
    }
    return [...Array(pageSize + 1)].map((e, i) => (
      <SkeletonCard key={i} number={i} />
    ));
  };

  const renderShowMoreButton = () => {
    if (results.results.length > pageSize * filters.page) {
      return (
        <button
          onClick={() =>
            handleFilterChange(
              "replace",
              { page: filters.page + 1 },
              filters,
              updateFilters
            )
          }
          className="btn-secondary"
        >
          Show more
        </button>
      );
    }
  };

  const renderFilters = () => {
    const topicsOptions = results.topicOptions.map((option) => ({
      name: option.text,
      value: option.value,
      isSelected: filters.topic.includes(option.value)
    }));

    const businessAreas = results.businessAreaOptions.map((area) => ({
      ...area,
      isSelected: filters.businessArea === area.value
    }));

    return (
      <Filters
        applyFilter={handleFiltering}
        sections={[
          {
            name: "topic",
            options: topicsOptions
          }
        ]}
        businessAreas={businessAreas}
        searchProps={{
          searchValue: filters.search,
          label: "Insights",
          doSearch: loadInsights
        }}
      />
    );
  };

  return (
    <>
      <ToastContainer />
      <div className="container container--narrow">
        {results && renderFilters()}
        <div className="row results">
          {renderResults()}
          {results?.results.length === 0 && (
            <div className="col-12">
              <div
                className="no-results"
                dangerouslySetInnerHTML={{
                  __html: results.noResultsContent
                }}
              ></div>
            </div>
          )}
        </div>
        <div className="row justify-content-center">
          {results && renderShowMoreButton()}
        </div>
      </div>
    </>
  );
}
